import { useOutsideClick } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useRichText } from '@bonusly/common-rich-text-editor';
import { useToast } from '@bonusly/design-system';
import { useDispatch } from '@bonusly/events';
import { useAnalytics } from '@bonusly/who-am-i';

import { Toast } from '../../../components/toast';
import { useSendMessage, useUpdateMessage } from '../../../hooks';
import { useModSubmit } from '../../../hooks/use-mod-submit';
import { EvaluationType } from '../../../lib';
import { AmplitudeEvent } from '../../../types/amplitude-event';
import { FeedbackEvent } from '../../../types/feedback-event';
import { QueryKey } from '../../../types/query-key';

interface UseMessageOptions {
	placeholder?: string;
}

export const useMessage = (feedbackId: string, type: EvaluationType, { placeholder = '' }: UseMessageOptions = {}) => {
	const dispatch = useDispatch();
	const toast = useToast();
	const { t } = useTranslation('feedback');
	const queryClient = useQueryClient();
	const cancelRef = useRef<HTMLButtonElement>(null);
	const { track } = useAnalytics();
	const [isFocused, setIsFocused] = useState(false);

	const { editor, html, text } = useRichText({
		onFocus: () => setIsFocused(true),
		placeholder,
	});

	const plainText = text.replace(/^\s+|\s+$/g, '');
	const isValid = plainText.length > 0;

	const invalidateQueries = (feedbackId: string) => {
		queryClient.invalidateQueries(['feedback-index']);
		queryClient.invalidateQueries(['feedback', feedbackId]);
		queryClient.invalidateQueries([QueryKey.ConsolidatedFeedbackIndex]);
	};

	const { mutate: createFeedbackMessage, isLoading: isLoadingCreateRequest } = useSendMessage({
		onSuccess: () => {
			dispatch(FeedbackEvent.MessageChanged);
			toast({
				position: 'bottom-right',
				render: () => {
					return <Toast isCloseable message={t('feedback.message.success')} status="success" />;
				},
			});
			editor?.commands.clearContent();
			invalidateQueries(feedbackId);
		},
	});
	const { mutate: updateFeedbackMessage, isLoading: isLoadingUpdateRequest } = useUpdateMessage({
		onSuccess: () => {
			dispatch(FeedbackEvent.MessageChanged);
			toast({
				position: 'bottom-right',
				render: () => {
					return <Toast isCloseable message={t('feedback.message.success')} status="success" />;
				},
			});
			editor?.commands.clearContent();
			invalidateQueries(feedbackId);
		},
	});

	const onSubmit = ({ publish, messageId }: { publish: boolean; messageId?: string }) => {
		if (!isValid) return;

		if (messageId) {
			updateFeedbackMessage({
				feedbackMessageId: messageId,
				text: html,
				publish,
			});
		} else {
			createFeedbackMessage({ feedbackId: feedbackId, text: html, publish });
		}

		const amplitudeCreateMessageEventMap = {
			[EvaluationType.Unrequested]: AmplitudeEvent.UnsolicitedFeedbackAddResponse,
			[EvaluationType.Requested]: AmplitudeEvent.RespondToRequest,
		};
		const amplitudeEventName = !publish ? AmplitudeEvent.SolicitedFeedbackDraftSaved : amplitudeCreateMessageEventMap[type];
		track(amplitudeEventName, { source: 'feedback page' });
	};

	const ref = useModSubmit(onSubmit);
	useOutsideClick({ ref, handler: () => setIsFocused(false) });

	const handleCancel = () => {
		editor?.commands.clearContent();
		setIsFocused(false);
	};

	return {
		cancelRef,
		editor,
		formRef: ref,
		isEditing: isFocused || plainText.length > 0,
		isSubmitting: isLoadingCreateRequest || isLoadingUpdateRequest,
		isValid,
		invalidateQueries,
		onCancel: handleCancel,
		onSubmit,
	};
};
